/**
 * A háttérprogram elérése.
 *
 * A felület minden adatot innen kap. Egyetlen helyen megy át minden kérés,
 * hogy a hibakezelés és a későbbi költözés (más cím, bejelentkezés) egy
 * ponton legyen intézhető.
 */

export class ApiHiba extends Error {
  constructor(
    message: string,
    readonly statusz: number,
  ) {
    super(message);
  }
}

/**
 * A belépett felhasználó adatai és jogai. (3.0 / 1.)
 * A szerver adja belépéskor és az `/api/en` lekérdezésre.
 */
export interface BelepettFelhasznalo {
  id: number;
  nev: string;
  felhasznalonev: string;
  szerepkor_id: number;
  szerepkor_nev: string;
  admin: boolean;
  sofor_id: number | null;
  sofor_nev: string | null;
  rendszam: string | null;
  email: string | null;
  van_alairas: boolean;
  nyomtato_beallitas: string | null;
  /** Menüpontonként 0–3, műveletenként 0 vagy 1. */
  jogok: Record<string, number>;
}

/** Menüpont a szerver listájából: kulcs, címke, csoport. */
export type MenuLeiro = [string, string, string];
/** Külön engedélyhez kötött művelet: kulcs, címke, és „admin", ha csak neki adható. */
export type MuveletLeiro = [string, string, string?];

export interface EnValasz {
  felhasznalo: BelepettFelhasznalo | null;
  menuk: MenuLeiro[];
  muveletek: MuveletLeiro[];
  /** Gyakorló példány: a felület figyelmeztető sávot mutat. (2026-09-29) */
  gyakorlo?: boolean;
}

async function kerd<T>(ut: string, beallitas: RequestInit = {}): Promise<T> {
  let valasz: Response;

  // A tartalomtípust CSAK akkor küldjük, ha tényleg van tartalom. Enélkül a
  // szerver joggal utasítja vissza a törlést: "azt mondtad, JSON jön, de nem jött".
  const fejlecek: Record<string, string> = { ...(beallitas.headers as Record<string, string>) };
  if (beallitas.body !== undefined && beallitas.body !== null) {
    fejlecek['Content-Type'] ??= 'application/json';
  }

  try {
    valasz = await fetch(ut, { ...beallitas, headers: fejlecek });
  } catch {
    throw new ApiHiba(
      'A háttérprogram nem válaszol. Fut még az a fekete ablak, ami elindította?',
      0,
    );
  }

  if (!valasz.ok) {
    let uzenet = `A művelet nem sikerült (${valasz.status}).`;
    try {
      // A saját hibáink a "hiba" mezőben jönnek; a keretrendszer sajátjai
      // (pl. hibás kérés) a "message"-ben. Mindkettőt megmutatjuk, mert egy
      // néma hibaszám senkinek nem segít.
      const test = (await valasz.json()) as { hiba?: string; message?: string };
      if (test.hiba) uzenet = test.hiba;
      else if (test.message) uzenet = `${uzenet} ${test.message}`;
    } catch {
      /* ha nem JSON jött vissza, marad az általános üzenet */
    }
    throw new ApiHiba(uzenet, valasz.status);
  }

  if (valasz.status === 204) return undefined as T;
  return (await valasz.json()) as T;
}

export const api = {
  get: <T>(ut: string) => kerd<T>(ut),
  post: <T>(ut: string, test?: unknown) =>
    kerd<T>(ut, { method: 'POST', body: JSON.stringify(test ?? {}) }),
  patch: <T>(ut: string, test?: unknown) =>
    kerd<T>(ut, { method: 'PATCH', body: JSON.stringify(test ?? {}) }),
  torol: <T>(ut: string) => kerd<T>(ut, { method: 'DELETE' }),
  /** Nyers szöveg küldése — a CSV visszatöltéshez. */
  szoveg: <T>(ut: string, tartalom: string) =>
    kerd<T>(ut, {
      method: 'POST',
      body: tartalom,
      headers: { 'Content-Type': 'text/csv; charset=utf-8' },
    }),
  /**
   * Fájl feltöltése nyers bájtokként — a járatlap-importhoz.
   *
   * A fájl nevét külön fejlécben küldjük, URL-kódolva: enélkül az ékezetes
   * fájlnév elromlana, a szervernek pedig a kiterjesztésből kell tudnia,
   * Excelt vagy CSV-t kapott-e.
   */
  fajl: <T>(ut: string, fajl: File) =>
    kerd<T>(ut, {
      method: 'POST',
      body: fajl,
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Fajlnev': encodeURIComponent(fajl.name),
      },
    }),
};

/* ------------------------------------------------------------------ */
/*  Adattípusok — ugyanaz, amit a szerver küld                         */
/* ------------------------------------------------------------------ */

export interface Etterem {
  id: number;
  nev: string;
  nev_kulcs: string;
  cegcsoport: string | null;
  irsz: string | null;
  cim: string | null;
  helyseg: string | null;
  netto_ar: number | null;
  brutto_ar: number | null;
  fplusz_netto: number | null;
  fplusz_brutto: number | null;
  mo_ar: number | null;
  r_ar: number | null;
  fizetesi_mod: string | null;
  megjegyzes: string | null;
  cegnev: string | null;
  adoszam: string | null;
  szekhely: string | null;
  mo_gyujtes: boolean;
  fo_gyujto: string | null;
  csak_gyujtes: boolean;
  aktiv: boolean;
  /** Háromjegyű partnerkód — a szerződésszám alapja. Üres, amíg nincs kiosztva. */
  kod: string | null;
  /** A cég, amelynek a telephelye. */
  ceg_id: number | null;

  /* --- 3.0 --- */
  /** KTJ azonosító — a begyűjtő okmányon szerepel. */
  ktj_azonosito?: string | null;
  /**
   * Kint lévő hordó a partnernél, **összesen**. `null` = nincs felmérve.
   * (A megrendelő döntése, 2026-11-04: „minden hordó hordó".)
   */
  hordo_kint?: number | null;
  /** Amit pótolni kell: többet vittünk el, mint amennyit otthagytunk. */
  hordo_potolando?: number;
  /** A régi, típusonkénti nyilvántartás — csak öröklött adat. */
  hordo_kint_hso?: number;
  hordo_kint_mo?: number;
  hordo_potolando_hso?: number;
  hordo_potolando_mo?: number;
}

/** Egy kiállított szállítólevél. (3.0 / 5.) */
export interface SzallitolevelSor {
  id: number;
  szam: string;
  tipus: 'HSO' | 'MO' | 'ALT' | 'MHJ';
  kelt: string;
  sofor: string | null;
  rendszam: string | null;
  tomeg_kg: number | null;
  hiteles_kg: number | null;
  szurt_kg: number | null;
  elvitt_hordo: number | null;
  otthagyott_hordo: number | null;
  kintlevo_hordo: number | null;
  potolando_hordo: number | null;
  tetel: string | null;
  ok: string | null;
  megjegyzes: string | null;
  iscc1: boolean;
  hely: 'auto' | 'raktar' | 'kiadva';
  /** Szállításra előkészítve: fóliázva, raklapon. A készletet nem mozdítja. */
  elokeszitve: boolean;
  elokeszitve_kelt: string | null;
  elokeszitve_ki: string | null;
  allapot: 'kiallitva' | 'sztornozva' | 'storno';
  email_kiment: boolean;
  storno_id: number | null;
  storno_ok: string | null;
  etterem_id: number | null;
  etterem_nev: string | null;
  etterem_cim: string | null;
  etterem_kod: string | null;
  cegnev: string | null;
  adoszam: string | null;
  ktj: string | null;
  tipus_nev: string;
  allapot_nev: string;
  /** Rakomány-e: számít-e az autó és a raktár készletébe. (2026-10-05) */
  rakomany: boolean;
  /** Hol van, kiírva. Ami nem rakomány, annál „—". */
  hely_nev: string;
}

export interface SzlevLista {
  sorok: SzallitolevelSor[];
  szuro_darabok: Record<string, number>;
  /** Minden sofőr, aki valaha állított ki okmányt — a szűrő listájához. */
  soforok: string[];
  /** Hány okmány felel meg a szűrésnek (a megjelenítési korlát előtt). */
  talalat: number;
  /** A szűrt okmányok összege, sztornó nélkül — a lista alján kiírjuk. */
  osszeg: {
    db: number;
    hso_kg: number;
    /** A moslékos okmányok tömege — a moslékhoz is írnak súlyt. */
    mo_kg: number;
    hso_hordo: number;
    mo_hordo: number;
  };
}

export interface EtteremSor extends Etterem {
  telephelyek: number;
  azonos_nevuek: number;
  telefon: string | null;
  /** A telephely **saját** e-mail címe (a sorban első). Null, ha nincs neki. */
  email: string | null;
  /** Ennek a címnek a kategóriája — mire jó. */
  email_cel: KapcsolatCel | null;
  /** Hány e-mail címe van összesen. */
  email_db: number;
  /** A cég címe — ide menne a levél, ha a telephelynek nincs sajátja. */
  ceg_email: string | null;
  /** A legfrissebb, le nem zárt MO szerződés lejárata. */
  mo_lejarat: string | null;
  /**
   * Mi hiányzik a számlázáshoz (cégnév, adószám, székhely) — a cég adatát
   * is beszámítva. Üres szöveg, ha minden megvan.
   */
  hianyzo_szamlazas: string;
}

/**
 * Mire jó egy e-mail cím. (3.0 / 5., 2026-09-30)
 *
 * A szerveren ugyanez a lista áll a sémában; itt azért ismételjük meg, mert
 * a felület önálló csomag, és nem hivatkozik a szerver kódjára.
 */
export const KAPCSOLAT_CELOK = ['szallito', 'szamla', 'egyeb'] as const;
export type KapcsolatCel = (typeof KAPCSOLAT_CELOK)[number];

/** Amit a felületen olvasunk a kategória helyén. */
export const KAPCSOLAT_CEL_NEVEK: Record<KapcsolatCel, string> = {
  szallito: 'Szállítólevél',
  szamla: 'Számla, felszólítás',
  egyeb: 'Egyéb, kapcsolattartás',
};

/** Rövid alak a szűk helyekre (sofőr telefonja, táblázat). */
export const KAPCSOLAT_CEL_ROVID: Record<KapcsolatCel, string> = {
  szallito: 'szállítólevél',
  szamla: 'számla',
  egyeb: 'egyéb',
};

export interface EtteremReszletek extends Etterem {
  telefonok: Array<{ id: number; ertek: string }>;
  emailek: Array<{ id: number; ertek: string; cel: KapcsolatCel }>;
  telephelyek: number;
}

export type Szuro =
  | 'mind'
  | 'olaj'
  | 'gyujtes'
  | 'nincsar'
  | 'duplikalt'
  | 'tobbtelephely'
  | 'rosszadoszam'
  | 'hianyos'
  | 'nincsemail'
  /** Akinél még nincs beírva a kint lévő hordó darabszáma. (2026-11-04) */
  | 'nincshordo'
  | 'inaktiv';

export interface EtteremLista {
  sorok: EtteremSor[];
  talalat: number;
  osszes: number;
  szuro_darabok: Record<Szuro, number>;
}

export interface Sofor {
  id: number;
  nev: string;
  rendszam: string | null;
  nyito_keszlet: number;
  aktiv: boolean;
  jarat_sorok: number;
  elszamolasok: number;
  torolheto: boolean;
  jelenlegi_keszlet: number;
  /** A hozzárendelt autó. (2.0) A rendszám szövegmező emiatt már csak öröklött adat. */
  auto_id: number | null;
  auto_rendszam: string | null;
}

export interface FizetesiMod {
  kod: string;
  nev: string;
  keszpenzben_befolyik: boolean;
  nyomtatasban_latszik: boolean;
  sorrend: number;
}

export type Nap = 'H' | 'K' | 'SZ' | 'CS' | 'P';
export type Gyakorisag = 'minden' | 'paros' | 'paratlan' | 'ho_utolso';

export interface AllandoSor {
  id: number;
  etterem_id: number;
  nap: Nap;
  gyakorisag: Gyakorisag;
  mo: number | null;
  fo: number | null;
  mennyiseg: number | null;
  fizetesi_mod: string | null;
  megjegyzes: string | null;
  sorrend: number;
  aktiv: boolean;

  etterem_nev: string;
  etterem_irsz: string | null;
  etterem_cim: string | null;
  etterem_helyseg: string | null;
  etterem_netto_ar: number | null;
  etterem_brutto_ar: number | null;
  etterem_csak_gyujtes: boolean;
  etterem_aktiv: boolean;
  etterem_fizetesi_mod: string | null;
}

export interface AllandoLista {
  sorok: AllandoSor[];
  nap_darabok: Record<Nap, number>;
  osszes: number;
  nap_nevek: Record<Nap, string>;
  gyakorisag_nevek: Record<Gyakorisag, string>;
}

/** Egy döntésre váró sor a régi Excelből. */
export interface VarolistaSor {
  nap: Nap;
  nap_neve: string;
  lap: string;
  excel_sor: number;
  fajlbeli_nev: string;
  mo: number | null;
  fo: number | null;
  mennyiseg: number | null;
  fizetesi_mod: string | null;
  megjegyzes: string | null;
  cim: string | null;
  gyakorisag: string;
  jeloltek: Array<{ etterem_id: number; nev: string; pontszam: number }>;
  tobbertelmu: boolean;
}

export interface VarolistaValasz {
  elerheto: boolean;
  fajl: string;
  sorok: VarolistaSor[];
}

/* ------------------------------------------------------------------ */
/*  Járatszervezés                                                     */
/* ------------------------------------------------------------------ */

export type NapStatusz = 'tervezes' | 'kiadva' | 'ellenorzes' | 'lezarva';
export type JaratRendezes = 'felvetel' | 'etterem' | 'irsz' | 'cim';

export interface NapAdat {
  letezik: boolean;
  id: number | null;
  datum: string;
  felirat: string;
  hetkoznap: Nap | null;
  het: number;
  statusz: NapStatusz;
  lezarta: string | null;
  lezarva_ekkor: string | null;
  cimek: number;
  /** Szabad szöveg a naphoz — csak a járatlapra kerül. (2026-09-28) */
  egyeb_cimek: string;
}

export interface NapokValasz {
  napok: NapAdat[];
  statusz_nevek: Record<NapStatusz, string>;
  rendezes_nevek: Record<JaratRendezes, string>;
}

export interface JaratSor {
  id: number;
  nap_id: number;
  etterem_id: number;
  sorrend: number;
  /**
   * A sofőrön belüli túraútvonal sorrendje. Üres, ha nincs megadva —
   * olyankor a nyomtatás a nap közös sorrendjét követi.
   */
  tura_sorrend: number | null;
  sofor_id: number | null;
  utolag: boolean;

  terv_mo: number | null;
  terv_fo: number | null;
  terv_liter: number | null;
  terv_liter_x: boolean;
  /** Kihelyezendő hordó. */
  terv_hordo: number | null;

  teny_mo: number | null;
  teny_fo: number | null;
  teny_liter: number | null;
  teny_hordo: number | null;
  kanna_ki: number | null;
  kanna_ki_kezi: boolean;
  kanna_vissza: number | null;

  /** F+ olaj-e: jelölő, nem mennyiség — a liter a mennyiségben áll. */
  fplusz: boolean;
  egysegar: number | null;
  fizetesi_mod: string | null;
  megjegyzes: string | null;

  etterem_nev: string;
  etterem_irsz: string | null;
  etterem_cim: string | null;
  etterem_helyseg: string | null;
  van_fplusz: boolean;
  torzs_ar: number | null;
  torzs_fplusz_ar: number | null;
  allando: boolean;
  allando_id: number | null;
  /** A partner egyedi moslékára, ha eltér az alapártól — a név mellé kerül. */
  mo_egyedi: number | null;
}

export interface JaratOsszesites {
  cimek: number;
  mo: number;
  fo: number;
  hordo: number;
  fplusz: number;
  liter: number;
  liter_osszes: number;
  raktari: number;
}

export interface Figyelmeztetes {
  fajta: 'nincs_mennyiseg' | 'duplikalt' | 'nincs_ar';
  szoveg: string;
  tetelek: number[];
}

/** Egy sofőr terhelése a kiosztó sávban. (2.0) */
export interface KiosztasSor {
  sofor_id: number | null;
  nev: string;
  cimek: number;
  liter: number;
}

export interface JaratNap {
  nap: NapAdat;
  sorok: JaratSor[];
  osszesites: JaratOsszesites;
  figyelmeztetesek: Figyelmeztetes[];
  /** Sofőrönkénti terhelés; az utolsó elem a beosztatlanoké. (2.0) */
  kiosztas: KiosztasSor[];
  /** Hány cím nincs kiosztva. (2.0) */
  kiosztatlan: number;
  /** A választható sofőrök a legördülőhöz. (2.0) */
  soforok: Array<{ id: number; nev: string }>;
  behuzhato: number;
  tervezheto: boolean;
}

/* ------------------------------------------------------------------ */
/*  Ellenőrzés                                                         */
/* ------------------------------------------------------------------ */

export interface EllenorzesSor {
  id: number;
  etterem_id: number;
  etterem_nev: string;
  etterem_irsz: string | null;
  etterem_cim: string | null;
  sorrend: number;
  /** A sofőrön belüli túraútvonal sorrendje — a nyomtatott lap szerint. */
  tura_sorrend: number | null;
  utolag: boolean;
  sofor_id: number | null;
  sofor_nev: string | null;

  terv_mo: number | null;
  terv_fo: number | null;
  terv_liter: number | null;
  terv_liter_x: boolean;
  terv_hordo: number | null;

  teny_mo: number | null;
  teny_fo: number | null;
  teny_liter: number | null;
  teny_hordo: number | null;
  kanna_ki: number | null;
  /** Kézzel írták-e be a kiadott kannát (különben a literből számolt). */
  kanna_ki_kezi: boolean;
  kanna_vissza: number | null;

  /** F+ olaj-e: jelölő, nem mennyiség. */
  fplusz: boolean;
  egysegar: number | null;
  fizetesi_mod: string | null;
  megjegyzes: string | null;
  van_fplusz: boolean;
  /** A partner egyedi moslékára, ha eltér az alapártól — a név mellé kerül. */
  mo_egyedi: number | null;
  osszeg: number;
}

export interface KategoriaOsszeg {
  kategoria_id: number | null;
  kategoria_nev: string;
  osszeg: number;
  keszpenzes: number;
}

/** Egy sofőr napi összesítése — a szerver számolja, a felület csak kiírja. */
export interface SoforOsszesites {
  liter: number;
  eladas: number;
  afa: number;
  atlagar: number | null;
  keszpenzes: number;
  nem_keszpenzes: number;

  mo_db: number;
  mo_osszeg: number;
  mo_keszpenzes: number;
  kanna_ki: number;
  kanna_vissza: number;
  kanna_osszeg: number;
  kanna_keszpenzes: number;
  hordo_db: number;
  hordo_osszeg: number;
  hordo_keszpenzes: number;
  fo_db: number;

  /** A kézzel felvett beszedés. */
  beszedes: number;
  beszedes_keszpenzes: number;
  kiadas: number;
  /** Ebből ami készpénzben ment ki. (2.0) */
  kiadas_keszpenzes: number;
  hso_osszeg: number;
  /** Ebből amit készpénzben fizettek ki. (2026-09-22) */
  hso_keszpenzes: number;
  /** A súly szerint átvett használt sütőolaj összsúlya. (2.0) */
  hso_kg: number;
  /** A kiadás a HSO nélkül (tankolás, kanna, egyéb), minden fizetési móddal. */
  egyeb_kiadas: number;
  /** Az F+ jelölésű sorok literje — a `liter`-ben is benne van. */
  fplusz_liter: number;
  /** A tankolások összege (az egyéb kiadásban is benne van). */
  tankolas_osszeg: number;
  /** A tankolások tételesen, rendszámmal. */
  tankolasok: TankolasTetel[];
  /** A mai HSO kilóár a termékek törzsből. (2.0) */
  hso_kilo_ar: number;
  raktari_liter: number;
  leado: number;
  beszedes_bontas: KategoriaOsszeg[];
  kiadas_bontas: KategoriaOsszeg[];
}

export interface SoforAllapot {
  sofor_id: number;
  nev: string;
  rendszam: string | null;
  sorok: number;
  /** Hány sorába nem írtak semmit — figyelmeztetés, nem tiltás. */
  uresen: number;
  uresen_nevek: string[];
  megkezdte: boolean;
  lezarva: boolean;
  lezarta: string | null;
  lezarva_ekkor: string | null;
  felrakodas: number | null;
  atadas: number | null;
  megszamolt_zaro: number | null;
  leadott_keszpenz: number | null;
  osszesites: SoforOsszesites;
  keszlet: {
    nyito: number;
    felrakodas: number | null;
    atadas: number | null;
    kiszallitva: number;
    szamitott_zaro: number;
    megszamolt_zaro: number | null;
    elteres: number | null;
    rendben: boolean;
  };
  /** Mit mondanak az aznapi szállítólevelei. Csak tájékoztatás. (2026-10-05) */
  okmanyok: {
    okmany_db: number;
    fo_kg: number;
    fo_hordo: number;
    mo_hordo: number;
    fo_elter: boolean;
    mo_elter: boolean;
    /**
     * Az okmányok egyenként: sorszám, fajta, kiállított hordó és tömeg.
     * Három moslékos okmány nem három hordó — ezért kell tételesen.
     */
    tetelek: Array<{ szam: string; tipus: string; hordo: number; kg: number }>;
  };
  penz: {
    szamitott_leado: number;
    leadott_keszpenz: number | null;
    elteres: number | null;
    rendben: boolean;
  };
  lezarhato: boolean;
  akadalyok: string[];
  /** Ami nem stimmel, de nem akadálya a lezárásnak. (2026-09-18) */
  figyelmeztetesek: string[];
}

export interface EllenorzesNap {
  nap: NapAdat;
  sorok: EllenorzesSor[];
  soforok: SoforAllapot[];
  kiosztatlan: number;
  kiosztatlan_nevek: string[];
  nap_lezarhato: boolean;
  nap_akadalyok: string[];
  osszesito: SoforOsszesites | null;
  /** Minden megkezdett sofőr összesen — az összesítő elszámolási laphoz. */
  osszesito_mind: SoforOsszesites | null;
  van_sofor: boolean;
}

export interface PenzTetel {
  id: number;
  nap_id: number;
  sofor_id: number;
  tipus: 'beszedes' | 'kiadas';
  kategoria_id: number | null;
  osszeg: number;
  fizetesi_mod: string | null;
  /** Súly kilogrammban — a HSO sornál. (2.0) */
  mennyiseg_kg: number | null;
  /** Hordó darabszám — a MO beszedés sornál. (2026-09-17) */
  mennyiseg_db: number | null;
  /** A tankolt autó rendszáma — a Tankolás sornál. (2026-09-17) */
  rendszam: string | null;
  megjegyzes: string | null;
  sorrend: number;
}

export interface TankolasTetel {
  penz_tetel_id: number;
  sofor_id: number;
  sofor_nev: string;
  rendszam: string | null;
  osszeg: number;
  fizetesi_mod: string | null;
  megjegyzes: string | null;
}

export interface EvAllapot {
  ev: number;
  napok: number;
  lezart_napok: number;
  nyitott_napok: string[];
  lezarva: { ido: string; felhasznalo: string; mentes: string } | null;
  lezarhato: boolean;
  akadaly: string | null;
}

export interface AutomentesAllapot {
  masodik_mappa: string | null;
  megtart_napok: number;
  utolso: { nev: string; keszult: string } | null;
  utolso_kora_nap: number | null;
  utolso_masolas: string | null;
  masolas_hiba: string | null;
}

/* ------------------------------------------------------------------ */
/*  Kimutatások                                                        */
/* ------------------------------------------------------------------ */

export type BontasRendezes = 'forgalom' | 'ertek' | 'etterem' | 'utolso' | 'cegcsoport';

export interface BontasSor {
  etterem_id: number | null;
  nev: string;
  cegcsoport: string | null;
  cim: string | null;
  csak_gyujtes: boolean;
  napok: Record<string, number>;
  becsult_napok: string[];
  becsult_liter: number;
  liter: number;
  ertek: number;
  hanyadik_honapja: number | null;
  utolso_vasarlas: string | null;
  vasarolt: boolean;
}

export interface NemVasarloSor {
  etterem_id: number;
  nev: string;
  cim: string | null;
  tipus: 'olajvasarlo' | 'csak_gyujtes';
  utolso_vasarlas: string | null;
  hanyadik_honapja: number | null;
}

export interface NapiBontasValasz {
  honap: string;
  napok: string[];
  lezart_napok: string[];
  elozmeny_napok: string[];
  sorok: BontasSor[];
  kartyak: Array<{ cimke: string; ertek: string; megjegyzes?: string }>;
  nem_vasarlok: NemVasarloSor[];
  osszes_liter: number;
  /** Ebből mennyi liter jön forgalmi előzményből. (2.0 / 6.3) */
  becsult_liter: number;
  osszes_ertek: number;
  napi_atlag: number;
  vasarolt_partner: number;
  nem_vasarolt_partner: number;
  raktari_liter: number;
  raktari_ertek: number;
  rendezes_nevek: Record<BontasRendezes, string>;
}

export interface PartnerkartonValasz {
  etterem: {
    id: number;
    nev: string;
    cegcsoport: string | null;
    cim: string | null;
    helyseg: string | null;
    netto_ar: number | null;
    brutto_ar: number | null;
    fplusz_brutto: number | null;
    mo_ar: number | null;
    r_ar: number | null;
    fizetesi_mod: string | null;
    cegnev: string | null;
    adoszam: string | null;
    szekhely: string | null;
    csak_gyujtes: boolean;
    megjegyzes: string | null;
  };
  vasarlasok: number;
  atlagos_rendeles: number | null;
  havi_atlag: number | null;
  atlagar: number | null;
  osszes_liter: number;
  /** Ebből mennyi liter jön forgalmi előzményből, ár nélkül. (2.0 / 6.3) */
  becsult_liter: number;
  osszes_ertek: number;
  afa: number;
  utolso_vasarlas: string | null;
  havi_bontas: Array<{ honap: string; liter: number; ertek: number }>;
  tetelek: Array<{
    /** Igaz, ha forgalmi előzmény: csak liter van benne. (2.0 / 6.3) */
    becsult: boolean;
    datum: string;
    liter: number;
    /** F+ olaj volt-e (jelölő). */
    fplusz: boolean;
    egysegar: number | null;
    ertek: number;
    fizetesi_mod: string | null;
    sofor_nev: string | null;
    mo: number | null;
    fo: number | null;
    megjegyzes: string | null;
  }>;
}

/* ------------------------------------------------------------------ */
/*  Autókészlet és archívum                                            */
/* ------------------------------------------------------------------ */

export interface AutoOszlop {
  sofor_id: number;
  nev: string;
  rendszam: string | null;
  aktiv: boolean;
}

export interface KeszletCella {
  volt: boolean;
  nyito: number;
  fel: number;
  le: number;
  szamitott_zaro: number;
  megszamolt_zaro: number | null;
  elteres: number | null;
  zaro: number;
}

/**
 * Üres hordó az autón, napi bontásban. (2026-11-04)
 * `null` = nincs felmérve: ennél a sofőrnél még nem rögzítettek felrakodást.
 */
export interface HordoCella {
  nyito: number | null;
  fel: number;
  le: number;
  zaro: number | null;
}

export interface KeszletNaploValasz {
  honap: string;
  autok: AutoOszlop[];
  sorok: Array<{
    datum: string;
    felirat: string;
    statusz: NapStatusz;
    cellak: Record<number, KeszletCella>;
    hordok: Record<number, HordoCella>;
  }>;
  zarok: Record<number, number>;
  elteresek: Record<number, number>;
  /** Üres hordó most az autón. `null` = nincs felmérve. */
  hordo_most: Record<number, number | null>;
  /** Tele hordó az autón: begyűjtve, a raktárnak még nem átadva. */
  tele_hordo: Record<number, number>;
}

/** Egy sofőr hordóállása — a felrakodás paneljéhez. (2026-11-04) */
export interface SoforHordo {
  sofor_id: number;
  nev: string;
  ures: number | null;
  tele: number;
  utolso_felrakodas: string | null;
}

/** Egy rögzített felrakodás (vagy hordókorrekció). */
export interface FelrakodasSor {
  id: number;
  sofor_id: number;
  sofor_nev: string;
  datum: string;
  idopont: string;
  fajta: 'felrakodas' | 'korrekcio';
  liter: number | null;
  ures_hordo: number | null;
  megjegyzes: string | null;
  rogzitette: string;
}

export interface AutokartonValasz {
  sofor_id: number;
  nev: string;
  rendszam: string | null;
  aktiv: boolean;
  indulo_keszlet: number;
  jelenlegi_keszlet: number;
  osszes_elteres: number;
  elteres_napok: number;
  mozgasok: Array<{
    datum: string;
    felirat: string;
    statusz: NapStatusz;
    nyito: number;
    fel: number;
    le: number;
    atadas: number;
    szamitott_zaro: number;
    megszamolt_zaro: number | null;
    elteres: number | null;
    egyenleg: number;
    cimek: number;
  }>;
}

export type Idoszak = '30nap' | '3honap' | 'honap' | 'ev' | 'mind';

export interface ArchivumValasz {
  idoszak_darabok: Record<Idoszak, number>;
  idoszak_nevek: Record<Idoszak, string>;
  szures: {
    idoszak: Idoszak;
    honap: string | null;
    ev: string | null;
    mettol: string | null;
    meddig: string | null;
    sajat_idoszak: boolean;
  };
  honapok: string[];
  evek: string[];
  sorok: Array<{
    datum: string;
    felirat: string;
    statusz: NapStatusz;
    cimek: number;
    terv_liter: number;
    teny_liter: number;
    kitoltott: number;
    lezart_soforok: number;
    megkezdett_soforok: number;
    leado: number;
    lezarta: string | null;
    lezarva_ekkor: string | null;
  }>;
  figyelmeztetesek: Array<{
    fajta: 'kimaradt' | 'sok_ellenorizetlen';
    szoveg: string;
    datumok: string[];
  }>;
  statusz_darabok: Record<NapStatusz, number>;
  statusz_nevek: Record<NapStatusz, string>;
}

/* ------------------------------------------------------------------ */
/*  Mentés és CSV visszatöltés                                         */
/* ------------------------------------------------------------------ */

export interface MentesFajl {
  nev: string;
  utvonal: string;
  meret: number;
  keszult: string;
}

export interface MentesekValasz {
  mentesek: MentesFajl[];
  mappa: string;
  adatbazis: { meret: number; ettermek: number; napok: number };
  automentes: AutomentesAllapot;
}

export interface CsvSorValtozas {
  csv_sor: number;
  id: number | null;
  nev: string;
  fajta: 'modosul' | 'uj' | 'valtozatlan' | 'hiba';
  mezok: Array<{ mezo: string; cimke: string; regi: unknown; uj: unknown }>;
  hiba?: string;
}

export interface CsvElonezetValasz {
  osszes_sor: number;
  modosul: number;
  uj: number;
  valtozatlan: number;
  hibas: number;
  erintetlen: number;
  valtozasok: CsvSorValtozas[];
}

/* --- járatlap-import (7.2) ----------------------------------------- */

export type ParositasMod =
  | 'pontos'
  | 'takaritott'
  | 'alias'
  | 'kezdet'
  | 'tisztitott'
  | 'nincs';

export interface ImportOszlop {
  fajta: string;
  index: number;
  betu: string;
  fejlec: string;
}

export interface MunkalapJelolt {
  nev: string;
  sorok: number;
  van_mennyiseg: boolean;
}

export interface ImportSor {
  fajl_sor: number;
  nev: string;
  cim: string | null;
  mo: number | null;
  fo: number | null;
  fplusz: number | null;
  mennyiseg: number | null;
  ar: number | null;
  fizetesi_mod: string | null;
  megjegyzes: string | null;
  kanna_ki: number | null;
  kanna_vissza: number | null;
  mod: ParositasMod;
  etterem_id: number | null;
  etterem_nev: string | null;
  jeloltek: Array<{ etterem_id: number; nev: string; pontszam: number }>;
  tobbertelmu: boolean;
  torzs_ar: number | null;
  duplikalt: boolean;
  ismeretlen_fizetes: boolean;
}

export interface ImportElemzes {
  datum: string;
  fajlnev: string;
  munkalap: string;
  munkalapok: string[];
  jelolt_lapok: MunkalapJelolt[];
  fejlec_sor: number;
  oszlopok: ImportOszlop[];
  sorok: ImportSor[];
  figyelmeztetesek: string[];
  osszes: number;
  parositva: number;
  parositatlan: number;
  jelenlegi_cimek: number;
  tervezheto: boolean;
}

export interface ImportEredmeny {
  felvett: number;
  uj_etterem: number;
  kihagyott: number;
  torolt: number;
  megjegyzett_parositas: number;
  jarat: JaratNap;
}

/* --- 2.0: termékek és autók ---------------------------------------- */

export interface TermekSor {
  id: number;
  kod: string;
  nev: string;
  egyseg: string;
  ar: number;
  brutto: boolean;
  afa_kulcs: number;
  tipus: string;
  megjegyzes: string | null;
  vedett: boolean;
  sorrend: number;
  aktiv: boolean;
  /** Számított: a nettó ár. `null` az „adat" típusnál. */
  netto: number | null;
  /** Számított: a bruttó ár. */
  brutto_ertek: number | null;
}

export interface TermekLista {
  sorok: TermekSor[];
  tipusok: string[];
}

export interface AutoSor {
  id: number;
  rendszam: string;
  tipus: string | null;
  kapacitas: number | null;
  muszaki_lejar: string | null;
  biztositas_lejar: string | null;
  szerviz_lejar: string | null;
  megjegyzes: string | null;
  aktiv: boolean;
  soforok: string[];
  sofor_db: number;
  torolheto: boolean;
  legkozelebbi_lejarat: number | null;
}

export interface LejaroOkmany {
  auto_id: number;
  rendszam: string;
  fajta: string;
  fajta_nev: string;
  datum: string;
  nap: number;
}

export interface AutoLista {
  sorok: AutoSor[];
  lejaro_okmanyok: LejaroOkmany[];
  okmany_nevek: Record<string, string>;
  figyelmeztetes_nap: number;
  osszesites: {
    aktiv: number;
    osszes: number;
    kapacitas: number;
    lejart: number;
    hamarosan: number;
  };
}

/* --- 2.0: cégek ---------------------------------------------------- */

export interface CegSor {
  id: number;
  nev: string;
  adoszam: string | null;
  szekhely: string | null;
  email: string | null;
  megjegyzes: string | null;
  aktiv: boolean;
  telephelyek: number;
  telephely_nevek: string[];
  nyitott_db: number;
  nyitott_osszeg: number;
  lejart_db: number;
  lejart_osszeg: number;
}

export interface CegLista {
  sorok: CegSor[];
  osszes: number;
  talalat: number;
  szuro_darabok: Record<string, number>;
  szuro_nevek: Record<string, string>;
  osszesites: {
    ceg: number;
    etterem: number;
    tobb_telephelyes: number;
    tobb_telephelyes_etterem: number;
    adoszam_nelkul: number;
    nyitott_osszeg: number;
  };
  labjegyzet: { telephelyek: number; nyitott_db: number; nyitott_osszeg: number };
}

export interface CegReszletek {
  ceg: CegSor;
  telephelyek: Array<{
    id: number;
    nev: string;
    irsz: string | null;
    cim: string | null;
    helyseg: string | null;
    brutto_ar: number | null;
    fizetesi_mod: string | null;
    csak_gyujtes: boolean;
    aktiv: boolean;
  }>;
  szamlak: Array<{
    id: number;
    bizonylatszam: string;
    telephely_nev: string | null;
    kelt: string | null;
    fizetesi_hatarido: string | null;
    brutto: number;
    jovairas_datum: string | null;
    storno: boolean;
    stornozva: boolean;
    keses: number;
  }>;
}

/* --- 2.0: szamlak --------------------------------------------------- */

export interface SzamlaSor {
  id: number;
  bizonylatszam: string;
  tomb: string | null;
  etterem_id: number | null;
  ceg_id: number | null;
  telephely_nev: string | null;
  cegnev: string | null;
  adoszam: string | null;
  kelt: string | null;
  fizetesi_hatarido: string | null;
  brutto: number;
  fizetesi_mod: string | null;
  storno: boolean;
  stornozva: boolean;
  hivatkozott_bizonylat: string | null;
  jovairas_datum: string | null;
  jovairas_forras: string | null;
  megjegyzes: string | null;
  /** Hány napja lejárt. 0, ha még nem. */
  keses: number;
  /** Nyitott-e: nincs jóváírva, nem stornó, nem készpénzes. */
  nyitott: boolean;
  /** Eddig befolyt összeg (részfizetésnél a részletek). */
  fizetett: number;
  /** Ami még hátravan. */
  hatralek: number;
  /** Részben fizetett: nyitott, de már jött rá pénz. */
  reszben: boolean;
  etterem_nev: string | null;
  ceg_nev: string | null;
}

/* --- 2.0 kiegészítés: számla-telephely párosítás ------------------ */

export interface ParositatlanTelephely {
  telephely_nev: string;
  kulcs: string;
  szamla_db: number;
  osszeg: number;
  cegnev: string | null;
  elso_szamla_id: number;
}

export interface TelephelyJavaslat {
  etterem_id: number;
  nev: string;
  cim: string | null;
  cegnev: string | null;
  pontszam: number;
  indok: string;
}

export interface TelephelyAdatok {
  telephely_nev: string;
  /** Mi köti össze a párosítandó számlákat: telephelynév, cégnév, vagy semmi. */
  csoport: 'telephely' | 'cegnev' | 'egyedi';
  szamla_db: number;
  cegnev: string | null;
  /** Ezen a néven venné fel az új éttermet; null, ha nincs miből nevet adni. */
  uj_etterem_nev: string | null;
  javaslatok: TelephelyJavaslat[];
}

export interface TelephelyParositasEredmeny {
  etterem_id: number;
  etterem_nev: string;
  szamla_db: number;
  uj_etterem: boolean;
  csoport: 'telephely' | 'cegnev' | 'egyedi';
}

export interface SzamlaLista {
  sorok: SzamlaSor[];
  osszes: number;
  talalat: number;
  szuro_darabok: Record<string, number>;
  szuro_nevek: Record<string, string>;
  osszesites: {
    nyitott_db: number;
    nyitott_osszeg: number;
    lejart_db: number;
    lejart_osszeg: number;
    parositatlan: number;
    /** Hány különböző telephelynév nincs párosítva. (2.0 kiegészítés) */
    parositatlan_telephely: number;
    parositatlan_nevvel: number;
    parositatlan_nev_nelkul: number;
  };
  szurt_osszeg: number;
}

export interface SzamlaImportElonezet {
  fajlnev: string;
  munkalap: string;
  oszlopok: Array<{ fajta: string; betu: string; fejlec: string }>;
  osszes: number;
  uj: number;
  mar_megvan: number;
  storno: number;
  keszpenzes: number;
  parositatlan_telephely: number;
  figyelmeztetesek: string[];
  parositatlan_nevek: string[];
}

export interface SzamlaImportEredmeny {
  felvett: number;
  kihagyott: number;
  /** Már bent volt, de a fájlban azóta megváltozott valami. */
  frissitett: number;
  /** Ebből ennyi kapott jóváírást: időközben kifizették. */
  jovairt: number;
  /** Korábban párosítatlan számla, ami most már talált telephelyet. */
  ujra_parositott: number;
  stornozott: number;
  /** Stornó bizonylatok, amelyek eredeti számlája nincs a rendszerben. (2026-09-25) */
  storno_eredeti_nelkul: string[];
  parositatlan: number;
  /** Kétszer szereplő bizonylatszámok a fájlban — csak az első kerül be. */
  duplikalt: string[];
}

/* --- 2.0: bankkivonatok -------------------------------------------- */

export interface BankTetelNezet {
  id: number;
  kivonat_id: number;
  ertek_nap: string | null;
  irany: string;
  osszeg: number;
  kod: string | null;
  partner_nev: string | null;
  kozlemeny: string | null;
  banki_hivatkozas: string | null;
  allapot: string;
  tisztazando_ok: string | null;
  pos_brutto: number | null;
  pos_jutalek: number | null;
  pos_terminal: string | null;
  pos_elfogadas: string | null;
  pos_kulcs_becsult: boolean;
  /** Ember döntött róla — az automatikus párosítás nem nyúl hozzá. */
  kezi_dontes: boolean;
  szamlak: Array<{ id: number; bizonylatszam: string; brutto: number; cegnev: string | null }>;
  indoklas: string | null;
  /** Ha a számlák után pénz maradt: a céghez rögzített túlfizetés. */
  tulfizetes: { osszeg: number; maradek: number; cegnev: string } | null;
}

/** Egy számla a kézi banki párosításhoz. */
export interface SzamlaJelolt {
  id: number;
  bizonylatszam: string;
  cegnev: string | null;
  telephely_nev: string | null;
  kelt: string | null;
  fizetesi_hatarido: string | null;
  brutto: number;
  fizetesi_mod: string | null;
  jovairas_datum: string | null;
  /** Kifizethető-e még. */
  nyitott: boolean;
  /** Eddig befolyt összeg (részfizetésnél). */
  fizetett: number;
  /** Ami még hátravan — ennyit fizethet ki a befizetés. */
  hatralek: number;
}

export interface BankJeloltek {
  /** Mennyit kell kiadnia a számláknak (kártyásnál a POS bruttó). */
  cel: number;
  /** Amit a program javasol — előre kijelölve. */
  javasolt: number[];
  jeloltek: SzamlaJelolt[];
  megjegyzes: string | null;
  /** A befizető cég, ha felismerhető — a túlfizetés alapértelmezett cége. */
  ceg_javaslat: string | null;
  /** Lehet-e túlfizetés (kártyás elszámolásnál nem). */
  tulfizetes_lehet: boolean;
}

/** Egy túlfizetés a Számlák → Túlfizetések fülön. */
export interface TulfizetesSor {
  id: number;
  bank_tetel_id: number;
  /** A pénz beérkezésének napja. */
  datum: string;
  cegnev: string;
  ceg_id: number | null;
  osszeg: number;
  felhasznalt: number;
  maradek: number;
  partner_nev: string | null;
  kozlemeny: string | null;
  megjegyzes: string | null;
  /** A cég nyitott számlái — ezekhez párosítható. */
  nyitott_szamlak: number;
  nyitott_osszeg: number;
  felhasznalasok: Array<{
    kifizetes_id: number;
    szamla_id: number;
    bizonylatszam: string;
    osszeg: number;
    rogzitve: string;
  }>;
}

export interface TulfizetesJeloltek {
  maradek: number;
  jeloltek: SzamlaJelolt[];
}

export interface BankAllapot {
  kivonatok: Array<{
    id: number;
    fajlnev: string;
    formatum: string;
    kivonat_nap: string | null;
    tranzakcio_db: number;
    jovairas_osszeg: number | null;
  }>;
  parositva: BankTetelNezet[];
  tisztazando: BankTetelNezet[];
  technikai: BankTetelNezet[];
  /** A hónapok, amikből van jóváírás, a legújabb elöl (csak a lekérdezésnél). */
  honapok?: Array<{ honap: string; tetelek: number }>;
  /** A megjelenített hónap; üres, ha minden hónap látszik. */
  honap?: string | null;
  egyenleg: {
    kivonat_jovairasai: number;
    technikai: number;
    parositva: number;
    tisztazando: number;
    elteres: number;
    rendben: boolean;
  };
}

export interface KartyasNap {
  nap: string;
  elszamolasok: number;
  netto: number;
  brutto: number;
  jutalek: number;
  tombok: Array<{
    tomb: string;
    terminal: string | null;
    pos_netto: number;
    csoport_brutto: number;
    jutalek: number;
    kulcs: number | null;
    becsult: boolean;
    szamlak: Array<{ bizonylatszam: string; cegnev: string | null; brutto: number }>;
  }>;
}

/* --- 2.0: adatbetoltes (6.) ------------------------------------------ */

export interface BetoltesAllapot {
  forgalom: {
    tetel: number;
    liter: number;
    legkorabbi: string | null;
    legkesobbi: string | null;
    ettermek: number;
  };
  /** A betöltés lépéseinek állapota: mi van már bent. */
  lepesek: {
    etterem: number;
    kodok: KodAllapot;
    szerzodes_mo: number;
    szerzodes_fo: number;
    szamla: number;
    kivonat: number;
  };
}

export interface ImportJelolt {
  etterem_id: number;
  nev: string;
  pontszam: number;
}

export interface SzerzodesImportSor {
  lap: string;
  fajl_sor: number;
  egyseg: string;
  cegnev: string;
  adoszam: string | null;
  cim: string | null;
  szerzodesszam: string | null;
  kotes: string | null;
  lejarat: string | null;
  darab: number | null;
  ar: number | null;
  nyilatkozat: boolean;
  mod: string;
  etterem_id: number | null;
  etterem_nev: string | null;
  jeloltek: ImportJelolt[];
  elavult: boolean;
}

export interface SzerzodesElonezet {
  fajta: 'mo' | 'fo';
  fajlnev: string;
  lapok: Array<{ nev: string; sorok: number }>;
  sorok: SzerzodesImportSor[];
  osszegzes: {
    fajl_sor: number;
    parositott: number;
    parositatlan: number;
    erintett_etterem: number;
    elavult: number;
    elo: number;
    lejart: number;
  };
}

export interface SzerzodesImportEredmeny {
  erintett: number;
  kihagyott: number;
  uj_alias: number;
  elo: number;
  lejart: number;
}

export interface ForgalomNevSor {
  nev: string;
  cim: string | null;
  tetelek: number;
  liter: number;
  mod: string;
  etterem_id: number | null;
  etterem_nev: string | null;
  jeloltek: ImportJelolt[];
}

export interface ForgalomElonezet {
  fajlnev: string;
  ev: number;
  lapok: Array<{ nev: string; napok: number; tetelek: number; liter: number }>;
  nevek: ForgalomNevSor[];
  osszegzes: {
    tetel: number;
    liter: number;
    nap: number;
    nev: number;
    parositott_nev: number;
    parositatlan_nev: number;
    parositatlan_liter: number;
  };
}

export interface ForgalomEredmeny {
  beirt: number;
  frissitett: number;
  valtozatlan: number;
  kihagyott_nev: number;
  kihagyott_tetel: number;
  uj_alias: number;
  liter: number;
}

/* --- 2.0: attekintes, adatminoseg, elmaradok, kereso ----------------- */

export interface Teendo {
  fajta: string;
  suly: 'surgos' | 'figyelem' | 'adathiany';
  szoveg: string;
  darab: number;
  ugras: { kepernyo: string; szuro?: string; datum?: string } | null;
  gomb: string;
}

export interface Attekintes {
  datum: string;
  szamok: Array<{ cimke: string; ertek: number; megjegyzes: string; kiemelt: boolean }>;
  teendok: Teendo[];
  honap: {
    cimke: string;
    liter: number;
    ertek: number;
    vasarolt_partner: number;
    napi_atlag: number;
  };
}

export interface Adatminoseg {
  ettermek: number;
  ellenorzesek: Array<{
    kulcs: string;
    cim: string;
    magyarazat: string;
    darab: number;
    fajta: 'javitando' | 'megjegyzendo';
    ugras: { kepernyo: string; szuro?: string } | null;
    gomb: string;
  }>;
}

export interface ElmaradoSor {
  etterem_id: number;
  nev: string;
  cim: string | null;
  cegnev: string | null;
  elozo_liter: number;
  mostani_liter: number;
  valtozas: number | null;
  utolso_vasarlas: string | null;
  fajta: 'elmaradt' | 'visszaeses' | 'uj' | 'valtozatlan';
}

export interface ElmaradoLista {
  honap: string;
  elozo_honap: string;
  sorok: ElmaradoSor[];
  szuro_darabok: Record<string, number>;
  szuro_nevek: Record<string, string>;
  osszesites: { elmaradt_liter: number; elmaradt_db: number; uj_db: number };
}

export interface KeresesEredmeny {
  q: string;
  osszes: number;
  csoportok: Array<{
    kulcs: string;
    cimke: string;
    osszes: number;
    talalatok: Array<{
      id: number;
      cim: string;
      alcim: string | null;
      ugras: { kepernyo: string; etterem_id?: number; q?: string };
    }>;
  }>;
}

/* --- 2.0: fizetesi felszolitas --------------------------------------- */

export interface FelszolitasTetel {
  id: number;
  bizonylatszam: string;
  telephely: string | null;
  kelt: string | null;
  fizetesi_hatarido: string | null;
  brutto: number;
  keses: number;
}

export interface FelszolitasSor {
  ceg_id: number;
  ceg_nev: string;
  adoszam: string | null;
  szekhely: string | null;
  email: string | null;
  /**
   * Honnan jött a cím: a telephelyek **számlázási** címe (ez az első),
   * a cég saját címe, vagy egy kategória nélküli telephelyi cím.
   */
  email_forras: 'telephely_szamla' | 'ceg' | 'telephely' | null;
  ceg_email: string | null;
  telephelyek: number;
  nyitott_db: number;
  nyitott_osszeg: number;
  lejart_db: number;
  lejart_osszeg: number;
  legnagyobb_keses: number;
  tetelek: FelszolitasTetel[];
}

export interface FelszolitasLista {
  sorok: FelszolitasSor[];
  osszes: number;
  talalat: number;
  szuro_darabok: Record<string, number>;
  szuro_nevek: Record<string, string>;
  osszesites: {
    erintett_ceg: number;
    lejart_osszeg: number;
    nyitott_osszeg: number;
    van_email: number;
    ceg_osszesen: number;
  };
  regi_hatar: number;
}

export interface LevelAdat {
  fajta: 'felszolitas' | 'egyenlegkozlo';
  kelt: string;
  sajat: { nev: string; szekhely: string; adoszam: string; bankszamla: string };
  cimzett: FelszolitasSor;
  osszesen: number;
  lejart_osszesen: number;
}

/* --- szerződések (szerzodesek-atepites.md) ------------------------------ */

/** Számolt állapot: élő, lejárt, újrakötve — vagy kézzel lezárva, helyszíni. */
export type SzerzAllapot = 'elo' | 'lejart' | 'ujra' | 'lezart' | 'helyszini' | 'kikuldott';

export interface SzerzodesNezet {
  id: number;
  tipus: 'MO' | 'FO';
  etterem_id: number | null;
  szam: string | null;
  partner_kod: string | null;
  kotes: string | null;
  lejarat: string | null;
  hordo_db: number | null;
  edeny_db: number | null;
  ar: number | null;
  nyilatkozat: boolean;
  megjegyzes: string | null;
  allapot: SzerzAllapot;
  /** Hány nap múlva jár le; negatív: ennyi napja lejárt. */
  nap: number | null;
  /** Nincs még végleges száma (a partnernek nincs kódja). */
  ideiglenes: boolean;
  helyszini: boolean;
  kod: string | null;
  nev: string;
  cegnev: string | null;
  adoszam: string | null;
  szekhely: string | null;
  cim: string | null;
  mo_ar: number | null;
}

export interface KodAllapot {
  kiosztva: boolean;
  kodos: number;
  kod_nelkul: number;
}

export interface SzerzOsszesites {
  mo_elo: number;
  mo_hamarosan: number;
  mo_lejart: number;
  nyilatkozat_hianyzik: number;
}

/** Az MO vagy FO fül: szerződésenként egy sor. */
export interface SzerzodesLista {
  sorok: SzerzodesNezet[];
  osszes: number;
  talalat: number;
  szuro_darabok: Record<string, number>;
  szuro_nevek: Record<string, string>;
  osszesites: SzerzOsszesites;
  kodok: KodAllapot;
}

/** Az Áttekintés: partnerenként egy sor. */
/**
 * Ki milyen szolgáltatást kap — a sales-mátrix egy sora.
 * (2026-10-08, a megrendelő kérése)
 */
export interface SzolgaltatasSor {
  etterem_id: number;
  kod: string | null;
  nev: string;
  cim: string | null;
  cegnev: string | null;
  adoszam: string | null;
  aktiv: boolean;
  /** Van-e olajára a törzsben. Ár nélkül nem is tud olajat venni. */
  van_ar: boolean;
  /** Vásárolt-e olajat a vizsgált időszakban. */
  olaj: boolean;
  olaj_liter: number;
  /** Az utolsó vásárlás — a teljes előzményből, nem csak az időszakból. */
  olaj_utolso: string | null;
  fo_szerz: 'van' | 'lejart' | 'nincs';
  mo_szerz: 'van' | 'lejart' | 'nincs';
  nyilatkozat: boolean;
  /** Tény: állítottunk-e ki rá begyűjtő okmányt. */
  hso_gyujtve: boolean;
  mo_gyujtve: boolean;
  gyujtes_utolso: string | null;
  telefon: string | null;
  email_szallito: string | null;
  email_szamla: string | null;
  email_egyeb: string | null;
}

export interface SzolgaltatasLista {
  sorok: SzolgaltatasSor[];
  talalat: number;
  osszes: number;
  idoszak: { tol: string; ig: string; honapok: number };
  osszesites: {
    vesz_de_nincs_szerz: number;
    szerz_de_nem_vesz: number;
    gyujtunk_de_nincs_szerz: number;
    semmi: number;
  };
}

export interface SzerzAttekintesSor {
  etterem_id: number;
  kod: string | null;
  nev: string;
  cim: string | null;
  cegnev: string | null;
  adoszam: string | null;
  aktiv: boolean;
  mo_gyujtes: boolean;
  fo_gyujto: string | null;
  /** Valóban elhoztunk-e tőle valamit: van rá kiállított begyűjtő okmány. */
  hso_gyujtve: boolean;
  mo_gyujtve: boolean;
  gyujtes_utolso: string | null;
  mo: SzerzodesNezet | null;
  fo: SzerzodesNezet | null;
  mo_ar: number | null;
  mo_elteres_szazalek: number | null;
}

export interface SzerzAttekintes {
  sorok: SzerzAttekintesSor[];
  osszes: number;
  talalat: number;
  szuro_darabok: Record<string, number>;
  szuro_nevek: Record<string, string>;
  osszesites: SzerzOsszesites;
  kodok: KodAllapot;
  /** A keresés felülírta-e a szűrőt, mert azon kívül is volt találat. */
  kereses_teljes: boolean;
}

export interface DokumentumAdat {
  ceg: {
    teljes_nev: string;
    rovid_nev: string;
    szekhely: string;
    adoszam: string;
    ksh: string;
    engedely: string;
    telefon: string;
    fax: string;
    email: string;
    kelt_hely: string;
    szerzodes_ev: number;
  };
  szerzodes: SzerzodesNezet;
  /** A partner adatai (helyszíninél a helyszínen felvettek). */
  partner: {
    nev: string;
    cegnev: string | null;
    adoszam: string | null;
    szekhely: string | null;
    ceg_szekhely: string | null;
    irsz: string | null;
    cim: string | null;
    helyseg: string | null;
    telefon: string | null;
  };
  termek: { hordo_liter: number; hordo_betetdij: number; hso_ar: number; mo_ar: number };
  beallitas: {
    szerzodesszam: string;
    kelt: string;
    hely: string;
    darab: number;
    ev: number;
    lejarat: string | null;
  };
}

export interface Alapadatok {
  fizetesi_modok: FizetesiMod[];
  kategoriak: Array<{ id: number; tipus: string; nev: string; sorrend: number }>;
  beallitasok: Record<string, string>;
  afa_kulcs: number;
  etterem_szurok: Record<Szuro, string>;
}
